import { HrefFilePayload } from 'shared/types/file-payload';

import { uploadEditorImage } from '../api';

function getBase64ByteLength(base64: string): number {
    const base64DecodedBytesPerQuartet = 3;
    const base64EncodedCharsPerQuartet = 4;

    const cleaned = base64.replace(/\s/g, '');
    const padding = cleaned.endsWith('==') ? 2 : cleaned.endsWith('=') ? 1 : 0;
    return Math.max(
        0,
        Math.floor((cleaned.length * base64DecodedBytesPerQuartet) / base64EncodedCharsPerQuartet) - padding
    );
}

function buildHrefPayloadFromDataUrl(dataUrl: string): HrefFilePayload {
    const match = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
    if (!match) {
        throw new Error('Invalid data URL');
    }

    const contentType = match[1];
    const base64Data = match[2];
    const subtype = contentType.split('/')[1] || 'png';
    const ext = subtype.split('+')[0] || 'png';

    return {
        contentType,
        filename: `image.${ext}`,
        href: dataUrl,
        size: getBase64ByteLength(base64Data),
        type: 'href'
    };
}

export async function handleImageUpload(source: File | string): Promise<string> {
    let payload: HrefFilePayload | { url: string };

    if (typeof source === 'string') {
        if (source.startsWith('data:')) {
            payload = buildHrefPayloadFromDataUrl(source);
        } else {
            // For URLs, send directly to server (let backend handle CORS/downloading)
            payload = { url: source };
        }
    } else {
        // For Files, convert to base64 HrefFilePayload
        const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(source);
        });

        payload = {
            contentType: source.type,
            filename: source.name,
            href: base64,
            size: source.size,
            type: 'href'
        };
    }

    // Call API to upload
    const response = await uploadEditorImage(payload);
    if (!response.success) {
        throw new Error(response.data?.error || 'Failed to upload image');
    }
    return response.data.url;
}
